import { cn } from "kz-ui-sdk";
import React, { useEffect, useRef, useState } from "react";
import styles from "./LoadingBar.module.css";

interface LoadingBarProps {
  loading: boolean;
  duration?: number; // Total time in milliseconds
  onComplete?: () => void; // Callback when loading reaches 100%
  width?: string | number;
  className?: string;
}

const LoadingBar: React.FC<LoadingBarProps> = ({ loading, duration = 5000, onComplete, width = "100%", className }) => {
  const [progress, setProgress] = useState(0);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const lastProgressRef = useRef(0);
  const isCompleteRef = useRef(false);

  useEffect(() => {
    const animate = (timestamp: number) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;

      const elapsed = timestamp - startTimeRef.current;
      const baseProgress = (elapsed / duration) * 100; // Linear progress

      // Randomize speed (50% - 150%) but correct near the end
      const randomFactor = Math.random() * 1 + 0.5; // 0.5x to 1.5x variation
      let newProgress = baseProgress * randomFactor;

      // Ensure it never overshoots 100% & correct progress if close to completion
      if (elapsed >= duration - 100) {
        newProgress = Math.min(100, baseProgress);
      }

      // Ensure progress never decreases
      const safeProgress = Math.max(newProgress, lastProgressRef.current);
      lastProgressRef.current = safeProgress;

      setProgress(safeProgress);

      if (safeProgress < 100) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else if (!isCompleteRef.current) {
        isCompleteRef.current = true;
        onComplete?.();
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      startTimeRef.current = null;
    };
  }, [loading, duration, onComplete]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  useEffect(() => {
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === containerRef.current) {
            setContainerWidth(entry.contentRect.width);
          }
        }
      });
      resizeObserver.observe(containerRef.current);
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn(styles["loading-container"], className)}
      style={{
        width,
      }}
    >
      <div
        className={styles["loading-bar"]}
        style={{
          width: `${progress}%`,
          backgroundSize: `${containerWidth}px 100%`,
        }}
      />
    </div>
  );
};

export default LoadingBar;
